import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import GoProWifi from "../services/goproWifi";
import { playRecordStartCue, playRecordStopCue, playWifiConnectedCue, playGoProDisconnectCue, speakGoProConnected, speakGoProDisconnected } from "../utils/audioCues";

// Open GoPro BLE protocol references:
// https://gopro.github.io/OpenGoPro/ble_2_0
//
// Control & Query service
const GOPRO_CTRL_SERVICE = "0000fea6-0000-1000-8000-00805f9b34fb";
const COMMAND_REQ_UUID = "b5f90072-aa8d-11e3-9046-0002a5d5c51b"; // write commands
const COMMAND_RSP_UUID = "b5f90073-aa8d-11e3-9046-0002a5d5c51b"; // notifications
const QUERY_REQ_UUID = "b5f90076-aa8d-11e3-9046-0002a5d5c51b";
const QUERY_RSP_UUID = "b5f90077-aa8d-11e3-9046-0002a5d5c51b";

// Wi-Fi credentials service (separate service exposed by camera)
const GOPRO_WIFI_SERVICE = "b5f90001-aa8d-11e3-9046-0002a5d5c51b";
const WIFI_SSID_UUID = "b5f90002-aa8d-11e3-9046-0002a5d5c51b";
const WIFI_PASSWORD_UUID = "b5f90003-aa8d-11e3-9046-0002a5d5c51b";

// Command bytes (length-prefixed)
const CMD_SHUTTER_START = new Uint8Array([0x03, 0x01, 0x01, 0x01]);
const CMD_SHUTTER_STOP = new Uint8Array([0x03, 0x01, 0x01, 0x00]);
// Set AP Mode (command id 0x17, payload 1B): 0x01 enable, 0x00 disable
const CMD_AP_ENABLE = new Uint8Array([0x03, 0x17, 0x01, 0x01]);
// Load Preset Group (command id 0x3E, payload 2B BE group id): 1000 = Video
const CMD_LOAD_PRESET_VIDEO = new Uint8Array([0x04, 0x3E, 0x02, 0x03, 0xE8]);
// Settings characteristic (Open GoPro "Set Setting"): write [len, id, valueLen, value].
const SETTINGS_REQ_UUID = "b5f90074-aa8d-11e3-9046-0002a5d5c51b";
const SETTING_ID_RESOLUTION = 0x02; // video resolution enum
const SETTING_ID_FPS = 0x03;        // frames-per-second enum
// Status IDs we care about
const STATUS_ID_BATTERY = 0x46; // Internal battery percentage (Open GoPro 2.0+)
const STATUS_ID_ENCODING = 0x0a;
const STATUS_ID_SD_REMAINING = 0x36; // 8B uint - kilobytes remaining (per Open GoPro spec)
const STATUS_ID_VIDEOS_TAKEN = 0x27; // legacy "video count"

const STORAGE_KEY = "the99.goproDeviceId";

// Helpers ------------------------------------------------------------

function bytesToDataView(bytes) {
  const buf = new ArrayBuffer(bytes.length);
  const view = new DataView(buf);
  for (let i = 0; i < bytes.length; i++) view.setUint8(i, bytes[i]);
  return view;
}

function dataViewToBytes(dv) {
  if (!dv) return new Uint8Array(0);
  const out = new Uint8Array(dv.byteLength);
  for (let i = 0; i < dv.byteLength; i++) out[i] = dv.getUint8(i);
  return out;
}

function bytesToString(bytes) {
  // ASCII / UTF-8 string from raw bytes (strip nulls)
  let end = bytes.length;
  while (end > 0 && bytes[end - 1] === 0) end--;
  return new TextDecoder("utf-8").decode(bytes.slice(0, end));
}

function readUintBE(bytes) {
  let v = 0;
  for (let i = 0; i < bytes.length; i++) v = v * 256 + bytes[i];
  return v;
}

// Open GoPro BLE packet header decoder.
// Header byte layouts:
//   00LLLLLL                     -> general, 6-bit length (0..63)
//   01LLLLLL LLLLLLLL            -> extended 13-bit length
//   10000000 LLLLLLLL LLLLLLLL   -> extended 16-bit length
//   1xxxxxxx                     -> continuation packet (no length, just payload)
// Returns { isContinuation, headerLen, msgLen } for header packets, or
// { isContinuation: true, headerLen: 1 } for continuation packets.
function decodeBlePacketHeader(bytes) {
  if (!bytes || bytes.length === 0) return null;
  const b0 = bytes[0];
  if ((b0 & 0x80) !== 0) {
    // Continuation packet
    return { isContinuation: true, headerLen: 1, msgLen: 0 };
  }
  const top2 = (b0 & 0x60) >> 5;
  if (top2 === 0) {
    return { isContinuation: false, headerLen: 1, msgLen: b0 & 0x1F };
  }
  if (top2 === 1) {
    if (bytes.length < 2) return null;
    const msgLen = ((b0 & 0x1F) << 8) | bytes[1];
    return { isContinuation: false, headerLen: 2, msgLen };
  }
  // top2 === 2: 16-bit length
  if (bytes.length < 3) return null;
  const msgLen = (bytes[1] << 8) | bytes[2];
  return { isContinuation: false, headerLen: 3, msgLen };
}

// Stateful reassembler for fragmented Open GoPro BLE notifications.
// Returns the complete message bytes (without the BLE header) once a full
// message has been received, or null if more fragments are pending.
function createPacketAssembler() {
  let buf = null;
  let expected = 0;
  return function feed(bytes) {
    const hdr = decodeBlePacketHeader(bytes);
    if (!hdr) return null;
    if (hdr.isContinuation) {
      if (!buf) return null; // stray continuation, drop
      const tail = bytes.slice(hdr.headerLen);
      const merged = new Uint8Array(buf.length + tail.length);
      merged.set(buf, 0);
      merged.set(tail, buf.length);
      buf = merged;
    } else {
      // Start of a new message
      buf = bytes.slice(hdr.headerLen);
      expected = hdr.msgLen;
    }
    if (buf.length >= expected && expected > 0) {
      const out = buf.slice(0, expected);
      buf = null;
      expected = 0;
      return out;
    }
    return null;
  };
}

// Parse a Query Response message (single-status read or push update).
// Input is the message bytes WITHOUT the BLE packetization header.
// Format (Open GoPro 2.0):
//   Synchronous responses (query IDs 0x12/0x13/0x52/0x53):
//     [query-id] [error-code] [status-id] [value-len] [value-bytes...] ...
//   Async push notifications (query IDs 0x92/0x93/0xA2/0xA3):
//     [query-id] [status-id] [value-len] [value-bytes...] ...
function parseStatusPacket(bytes, target) {
  if (bytes.length < 1) return;
  const queryId = bytes[0];
  // Settings responses (0x12 get / 0x13 get-all) share the status TLV structure
  // but use the SETTING id namespace — parse those ids only here so a status
  // push (0x93) whose id happens to be 2/3 isn't misread as resolution/fps.
  const isSettingsResponse = queryId === 0x12 || queryId === 0x13;
  // Sync query responses include an error-code byte before the TLV stream.
  // Push notifications do not.
  const isSyncResponse =
    isSettingsResponse ||
    queryId === 0x52 || queryId === 0x53;
  let i = isSyncResponse ? 2 : 1;
  while (i + 1 < bytes.length) {
    const statusId = bytes[i++];
    const valueLen = bytes[i++];
    if (valueLen === 0 || i + valueLen > bytes.length) break;
    const valueBytes = bytes.slice(i, i + valueLen);
    i += valueLen;
    if (isSettingsResponse) {
      if (statusId === SETTING_ID_RESOLUTION) target.resolution = readUintBE(valueBytes);
      else if (statusId === SETTING_ID_FPS) target.fps = readUintBE(valueBytes);
      continue;
    }
    if (statusId === STATUS_ID_BATTERY && valueLen >= 1) {
      target.batteryPct = valueBytes[0];
    } else if (statusId === STATUS_ID_ENCODING && valueLen >= 1) {
      target.encoding = !!valueBytes[0];
    } else if (statusId === STATUS_ID_SD_REMAINING && valueLen >= 1) {
      // GoPro reports SD remaining in kilobytes; convert to bytes for display.
      target.sdRemainingBytes = readUintBE(valueBytes) * 1024;
    } else if (statusId === STATUS_ID_VIDEOS_TAKEN && valueLen >= 1) {
      target.videoCount = readUintBE(valueBytes);
    }
  }
}

// ---------------------------------------------------------------------

export default function useGoPro() {
  const [available, setAvailable] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [devices, setDevices] = useState([]);
  const [connected, setConnected] = useState(null);
  const [recording, setRecording] = useState(false);
  const [status, setStatus] = useState({
    batteryPct: null,
    encoding: null,
    sdRemainingBytes: null,
    videoCount: null,
    resolution: null,
    fps: null,
  });
  const [wifi, setWifi] = useState({ connected: false, ssid: null });
  const [downloadProgress, setDownloadProgress] = useState(null); // { bytesRead, totalBytes }
  const [error, setError] = useState(null);

  const bleRef = useRef(null);
  const wifiCredsRef = useRef(null); // { ssid, password }
  const intentionalDisconnectRef = useRef(false); // suppress disconnect alert on user-initiated disconnects
  const downloadListenerRef = useRef(null);
  // Last resolution/FPS the angler picked, re-applied after a preset load
  // (which can reset them) so recordings use the chosen settings.
  const lastResRef = useRef(null);
  const lastFpsRef = useRef(null);
  // Consecutive failed status polls — used to detect a silently-dropped BLE link.
  const bleFailCountRef = useRef(0);
  const isNative = typeof Capacitor !== "undefined" && Capacitor.isNativePlatform();

  // Lazy-load + initialize the BLE plugin. Extracted so it can be retried on
  // demand (e.g. when the user taps "Scan" before the initial init finished,
  // or after enabling Bluetooth / granting permissions). Idempotent: once
  // bleRef is set it just re-flags availability without re-initializing.
  const ensureBleReady = useCallback(async () => {
    if (!isNative) {
      setAvailable(false);
      return false;
    }
    if (!bleRef.current) {
      const mod = await import("@capacitor-community/bluetooth-le");
      bleRef.current = mod.BleClient;
    }
    await bleRef.current.initialize({ androidNeverForLocation: true });
    setAvailable(true);
    return true;
  }, [isNative]);

  // Lazy-load BLE plugin on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureBleReady();
      } catch (e) {
        if (!cancelled) {
          setError(e);
          setAvailable(false);
        }
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureBleReady]);

  // GoPro Wi-Fi download progress listener
  useEffect(() => {
    if (!isNative) return;
    let sub;
    (async () => {
      try {
        sub = await GoProWifi.addListener("downloadProgress", (ev) => {
          setDownloadProgress({ bytesRead: ev.bytesRead, totalBytes: ev.totalBytes });
        });
      } catch (_) {}
    })();
    return () => {
      if (sub && sub.remove) sub.remove();
    };
  }, [isNative]);

  // -------------------------------------------------------------------
  // BLE: scan / connect / disconnect / shutter / status
  // -------------------------------------------------------------------

  const scan = useCallback(
    async ({ timeoutMs = 10000 } = {}) => {
      // Init on demand so tapping "Scan" can recover from a not-yet-ready or
      // failed initial init (e.g. Bluetooth was off / permissions pending).
      if (!bleRef.current) {
        await ensureBleReady();
      }
      if (!bleRef.current) {
        throw new Error("Bluetooth unavailable. Enable Bluetooth and grant permissions, then try again.");
      }
      setError(null);
      setDevices([]);
      setScanning(true);
      const found = new Map();
      try {
        await bleRef.current.requestLEScan(
          { services: [GOPRO_CTRL_SERVICE], allowDuplicates: false },
          (result) => {
            const id = result?.device?.deviceId;
            if (!id) return;
            const name = result?.device?.name || result?.localName || "GoPro";
            found.set(id, { deviceId: id, name, rssi: result?.rssi ?? null });
            setDevices(Array.from(found.values()));
          }
        );
        await new Promise((r) => setTimeout(r, timeoutMs));
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        try { await bleRef.current.stopLEScan(); } catch (_) {}
        setScanning(false);
      }
      return Array.from(found.values());
    },
    [ensureBleReady]
  );

  const connect = useCallback(async (device) => {
    if (!bleRef.current) throw new Error("BLE not initialized");
    const deviceId = typeof device === "string" ? device : device?.deviceId;
    const name = typeof device === "object" ? device?.name : null;
    if (!deviceId) throw new Error("connect requires a deviceId");
    setError(null);
    try {
      // GoPro can be slow to accept the GATT connection on the first attempt,
      // so give it more headroom than the plugin's 10s default.
      await bleRef.current.connect(deviceId, () => {
        // Fires on any link drop. Distinguish an unexpected disconnect (camera
        // out of range / powered off) from a user-initiated one so we only
        // sound the alert for genuine losses.
        if (!intentionalDisconnectRef.current) {
          try { playGoProDisconnectCue(); } catch (_) {}
          try { speakGoProDisconnected(); } catch (_) {}
        }
        intentionalDisconnectRef.current = false;
        setConnected(null);
        setRecording(false);
        wifiCredsRef.current = null;
      }, { timeout: 20000 });

      // Per the Open GoPro spec the client must bond with the camera; without a
      // bond the Hero drops the link, which surfaces as a connection timeout.
      // Bonding is Android-only (iOS handles it at the OS level).
      if (Capacitor.getPlatform() === "android") {
        try {
          const alreadyBonded = await bleRef.current.isBonded(deviceId);
          if (!alreadyBonded) await bleRef.current.createBond(deviceId, { timeout: 20000 });
        } catch (_) {}
      }

      // Subscribe to query responses (status pushes + reads). GoPro fragments
      // multi-status replies across BLE notifications, so reassemble first.
      try {
        const assembler = createPacketAssembler();
        await bleRef.current.startNotifications(
          deviceId,
          GOPRO_CTRL_SERVICE,
          QUERY_RSP_UUID,
          (dv) => {
            try {
              const fragment = dataViewToBytes(dv);
              const message = assembler(fragment);
              if (!message) return;
              const parsed = {};
              parseStatusPacket(message, parsed);
              if (Object.keys(parsed).length) {
                setStatus((prev) => ({ ...prev, ...parsed }));
                if (parsed.encoding !== undefined) setRecording(parsed.encoding);
              }
            } catch (_) {}
          }
        );
      } catch (_) {}

      // Subscribe to command responses (best-effort)
      try {
        await bleRef.current.startNotifications(
          deviceId,
          GOPRO_CTRL_SERVICE,
          COMMAND_RSP_UUID,
          () => {}
        );
      } catch (_) {}

      setConnected({ deviceId, name: name || "GoPro" });
      try { speakGoProConnected(); } catch (_) {}
      try { window.localStorage?.setItem(STORAGE_KEY, deviceId); } catch (_) {}
      return deviceId;
    } catch (e) {
      setError(e);
      throw e;
    }
  }, []);

  const disconnect = useCallback(async () => {
    if (!bleRef.current || !connected?.deviceId) return;
    // Mark this as a deliberate disconnect so the onDisconnect handler doesn't
    // sound the "camera lost" alert.
    intentionalDisconnectRef.current = true;
    try { await bleRef.current.disconnect(connected.deviceId); } catch (e) { setError(e); }
    setConnected(null);
    setRecording(false);
    wifiCredsRef.current = null;
  }, [connected]);

  const writeShutter = useCallback(async (start) => {
    if (!bleRef.current) throw new Error("BLE not initialized");
    if (!connected?.deviceId) throw new Error("Not connected to a GoPro");
    const bytes = start ? CMD_SHUTTER_START : CMD_SHUTTER_STOP;
    await bleRef.current.write(
      connected.deviceId,
      GOPRO_CTRL_SERVICE,
      COMMAND_REQ_UUID,
      bytesToDataView(bytes)
    );
    setRecording(!!start);
    try { (start ? playRecordStartCue : playRecordStopCue)(); } catch (_) {}
  }, [connected]);

  const startRecording = useCallback(async () => {
    if (!bleRef.current) throw new Error("BLE not initialized");
    if (!connected?.deviceId) throw new Error("Not connected to a GoPro");
    // Ensure camera is in Video mode before pressing the shutter, otherwise
    // it will use whatever preset is loaded (e.g. Photo).
    try {
      await bleRef.current.write(
        connected.deviceId,
        GOPRO_CTRL_SERVICE,
        COMMAND_REQ_UUID,
        bytesToDataView(CMD_LOAD_PRESET_VIDEO)
      );
      // Small delay so the camera settles into video mode before shutter.
      await new Promise((r) => setTimeout(r, 250));
      // Loading the Video preset group can reset resolution/FPS — re-apply the
      // angler's chosen values so the recording uses them.
      const reapply = async (settingId, value) => {
        if (value == null) return;
        try {
          await bleRef.current.write(
            connected.deviceId,
            GOPRO_CTRL_SERVICE,
            SETTINGS_REQ_UUID,
            bytesToDataView(new Uint8Array([0x03, settingId, 0x01, value & 0xff]))
          );
        } catch (_) {}
      };
      if (lastResRef.current != null || lastFpsRef.current != null) {
        await reapply(SETTING_ID_RESOLUTION, lastResRef.current);
        await reapply(SETTING_ID_FPS, lastFpsRef.current);
        await new Promise((r) => setTimeout(r, 150));
      }
    } catch (_) { /* best-effort */ }
    return writeShutter(true);
  }, [connected, writeShutter]);
  const stopRecording = useCallback(() => writeShutter(false), [writeShutter]);

  // Send query for specific status IDs.
  // Open GoPro 2.0:
  //   0x52 = Get Status Values By ID (response also 0x52)
  //   0x53 = Register for Status Value Updates (response 0x53, pushes 0x93)
  const refreshStatus = useCallback(async () => {
    if (!bleRef.current || !connected?.deviceId) return;
    const ids = [
      STATUS_ID_BATTERY,
      STATUS_ID_ENCODING,
      STATUS_ID_SD_REMAINING,
      STATUS_ID_VIDEOS_TAKEN,
    ];
    try {
      // Read once
      await bleRef.current.write(
        connected.deviceId,
        GOPRO_CTRL_SERVICE,
        QUERY_REQ_UUID,
        bytesToDataView(new Uint8Array([1 + ids.length, 0x52, ...ids]))
      );
      bleFailCountRef.current = 0;
      // Subscribe to push updates so values stay live (best-effort)
      try {
        await bleRef.current.write(
          connected.deviceId,
          GOPRO_CTRL_SERVICE,
          QUERY_REQ_UUID,
          bytesToDataView(new Uint8Array([1 + ids.length, 0x53, ...ids]))
        );
      } catch (_) {}
    } catch (e) {
      // A dropped BLE link surfaces as "Not connected to device" on writes, but
      // the onDisconnect callback doesn't always fire — so the app keeps showing
      // "connected" while every command (delete/upload/record) silently fails.
      // After a couple of consecutive failures, reset so the UI prompts reconnect.
      if (/not connected/i.test(e?.message || "")) {
        bleFailCountRef.current = (bleFailCountRef.current || 0) + 1;
        if (bleFailCountRef.current >= 2) {
          bleFailCountRef.current = 0;
          if (!intentionalDisconnectRef.current) {
            try { playGoProDisconnectCue(); } catch (_) {}
            try { speakGoProDisconnected(); } catch (_) {}
          }
          setConnected(null);
          setRecording(false);
          setWifi({ connected: false, ssid: null });
        }
      }
    }
  }, [connected]);

  // Read the camera's current video resolution + FPS (Open GoPro 0x12 "Get
  // Setting Values By ID"). The response arrives on QUERY_RSP and is parsed
  // into status.resolution / status.fps.
  const refreshSettings = useCallback(async () => {
    if (!bleRef.current || !connected?.deviceId) return;
    const ids = [SETTING_ID_RESOLUTION, SETTING_ID_FPS];
    const pkt = new Uint8Array([1 + ids.length, 0x12, ...ids]);
    try {
      await bleRef.current.write(
        connected.deviceId,
        GOPRO_CTRL_SERVICE,
        QUERY_REQ_UUID,
        bytesToDataView(pkt)
      );
    } catch (_) {}
  }, [connected]);

  // Write a single setting (Open GoPro "Set Setting" TLV). Optimistically
  // reflects the value, then re-reads so the UI shows what the camera actually
  // accepted (invalid resolution/FPS combos are rejected by the camera).
  const setSetting = useCallback(async (settingId, value) => {
    if (!bleRef.current) throw new Error("BLE not initialized");
    if (!connected?.deviceId) throw new Error("Not connected to a GoPro");
    const v = value & 0xff;
    await bleRef.current.write(
      connected.deviceId,
      GOPRO_CTRL_SERVICE,
      SETTINGS_REQ_UUID,
      bytesToDataView(new Uint8Array([0x03, settingId, 0x01, v]))
    );
    if (settingId === SETTING_ID_RESOLUTION) {
      lastResRef.current = v;
      setStatus((prev) => ({ ...prev, resolution: v }));
    } else if (settingId === SETTING_ID_FPS) {
      lastFpsRef.current = v;
      setStatus((prev) => ({ ...prev, fps: v }));
    }
    try { await new Promise((r) => setTimeout(r, 300)); await refreshSettings(); } catch (_) {}
  }, [connected, refreshSettings]);

  const setVideoResolution = useCallback((value) => setSetting(SETTING_ID_RESOLUTION, value), [setSetting]);
  const setVideoFps = useCallback((value) => setSetting(SETTING_ID_FPS, value), [setSetting]);

  // -------------------------------------------------------------------
  // Wi-Fi: read creds, enable AP, connect device to GoPro AP
  // -------------------------------------------------------------------

  const readWifiCredentials = useCallback(async () => {
    if (!bleRef.current || !connected?.deviceId) throw new Error("Not connected");
    const ssidDv = await bleRef.current.read(
      connected.deviceId,
      GOPRO_WIFI_SERVICE,
      WIFI_SSID_UUID
    );
    const pwdDv = await bleRef.current.read(
      connected.deviceId,
      GOPRO_WIFI_SERVICE,
      WIFI_PASSWORD_UUID
    );
    const ssid = bytesToString(dataViewToBytes(ssidDv));
    const password = bytesToString(dataViewToBytes(pwdDv));
    wifiCredsRef.current = { ssid, password };
    return { ssid, password };
  }, [connected]);

  const enableAp = useCallback(async () => {
    if (!bleRef.current || !connected?.deviceId) throw new Error("Not connected");
    await bleRef.current.write(
      connected.deviceId,
      GOPRO_CTRL_SERVICE,
      COMMAND_REQ_UUID,
      bytesToDataView(CMD_AP_ENABLE)
    );
  }, [connected]);

  const connectWifi = useCallback(async () => {
    if (!isNative) throw new Error("Wi-Fi join requires native build");
    if (!connected?.deviceId) throw new Error("Connect to GoPro via Bluetooth first");
    let creds = wifiCredsRef.current;
    if (!creds) creds = await readWifiCredentials();
    await enableAp();
    // Camera takes ~2-4s to broadcast AP
    await new Promise((r) => setTimeout(r, 3000));
    const res = await GoProWifi.connect({
      ssid: creds.ssid,
      password: creds.password,
      timeoutMs: 30000,
    });
    setWifi({ connected: !!res?.connected, ssid: creds.ssid });
    if (res?.connected) {
      try { playWifiConnectedCue(); } catch (_) {}
    }
    return res;
  }, [isNative, connected, readWifiCredentials, enableAp]);

  const disconnectWifi = useCallback(async () => {
    if (!isNative) return;
    try { await GoProWifi.disconnect(); } catch (_) {}
    setWifi({ connected: false, ssid: null });
    setDownloadProgress(null);
  }, [isNative]);

  // -------------------------------------------------------------------
  // HTTP: list media + download a clip
  // -------------------------------------------------------------------

  const listMedia = useCallback(async () => {
    if (!isNative) throw new Error("native only");
    // Clear any lingering download bar from a prior transfer so a refreshed
    // list never shows a phantom "downloading…" card.
    setDownloadProgress(null);
    const res = await GoProWifi.httpGet({
      url: "http://10.5.5.9:8080/gopro/media/list",
      timeoutMs: 15000,
    });
    if (res.status < 200 || res.status >= 400) {
      throw new Error(`Media list HTTP ${res.status}`);
    }
    let json;
    try { json = JSON.parse(res.body); } catch (e) {
      throw new Error("Media list returned invalid JSON");
    }
    // Flatten: [{ folder, name, size, url }]
    const out = [];
    const media = json?.media || [];
    // GoPro stores each clip's `cre` as the camera's wall-clock time treated as
    // if it were UTC (the media list carries no timezone). Left uncorrected, JS
    // shifts it by the device's timezone and shows the wrong time (e.g. 4h off
    // in EDT). Add the local offset back so the instant renders as the original
    // wall-clock time locally, and also lines up with the real UTC session
    // timestamps used for certify-eligibility matching.
    const tzOffsetMs = new Date().getTimezoneOffset() * 60000;
    for (const dir of media) {
      const folder = dir.d;
      for (const f of dir.fs || []) {
        const url = `http://10.5.5.9:8080/videos/DCIM/${folder}/${f.n}`;
        // NOTE: We deliberately do NOT fetch each clip to parse MP4 atoms here.
        // GoPro stores the `moov` atom at the END of the file, so a bounded
        // head-range request can't recover the creation time — and the native
        // httpGet ignores the Range header, so it would download entire multi-GB
        // clips into memory and hang the listing (nothing would return).
        // The camera already provides a creation timestamp (`cre`) in the media
        // list, which is sufficient for session-match certify eligibility.
        const createdSec = Number(f.cre) || 0;
        const createdMs = createdSec ? createdSec * 1000 + tzOffsetMs : null;
        out.push({
          folder,
          name: f.n,
          size: Number(f.s) || 0,
          createdAt: createdMs,
          url,
          mp4CreationTime: createdMs ? new Date(createdMs) : null,
          mp4DurationSec: null,
        });
      }
    }
    // Newest first by createdAt then by name
    out.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0) || b.name.localeCompare(a.name));
    return out;
  }, [isNative]);

  // Delete a single clip on the GoPro SD card.
  // Open GoPro HTTP API: GET /gopro/media/delete/file?path=<folder>/<name>
  // NOTE: GoPro requires a literal slash in the path query value (not %2F),
  // so we only encode the folder and filename segments individually.
  const deleteClip = useCallback(async (clip) => {
    if (!isNative) throw new Error("native only");
    if (!clip?.folder || !clip?.name) throw new Error("clip.folder and clip.name required");
    const path = `${encodeURIComponent(clip.folder)}/${encodeURIComponent(clip.name)}`;
    const url = `http://10.5.5.9:8080/gopro/media/delete/file?path=${path}`;
    let res;
    try {
      res = await GoProWifi.httpGet({ url, timeoutMs: 15000 });
    } catch (e) {
      throw new Error(`Delete request failed: ${e?.message || e}`);
    }
    if (res.status < 200 || res.status >= 400) {
      const body = (res?.body || "").toString().slice(0, 200);
      throw new Error(`Delete HTTP ${res.status}${body ? `: ${body}` : ""}`);
    }
    return true;
  }, [isNative]);

  const downloadClip = useCallback(async (clip) => {
    if (!isNative) throw new Error("native only");
    if (!clip?.url || !clip?.name) throw new Error("clip.url and clip.name required");
    setDownloadProgress({ bytesRead: 0, totalBytes: clip.size || 0 });
    try {
      const res = await GoProWifi.httpDownload({
        url: clip.url,
        fileName: clip.name,
        timeoutMs: 600000,
      });
      return res; // { path, size, uri }
    } finally {
      // leave progress state alone for UI to display final value
    }
  }, [isNative]);

  // Fetch the GoPro-generated JPEG thumbnail for a clip and return it as a
  // data URI suitable for an <img src>. Open GoPro HTTP API:
  //   GET /gopro/media/thumbnail?path=<folder>/<name>
  // The camera must be on Wi-Fi (boundNetwork) for this to succeed. Returns
  // null on any failure so callers can fall back to a placeholder.
  const getThumbnail = useCallback(async (clip) => {
    if (!isNative) return null;
    if (!clip?.folder || !clip?.name) return null;
    const path = `${encodeURIComponent(clip.folder)}/${encodeURIComponent(clip.name)}`;
    const url = `http://10.5.5.9:8080/gopro/media/thumbnail?path=${path}`;
    try {
      const res = await GoProWifi.httpGetBase64({ url, timeoutMs: 12000 });
      if (!res || res.status < 200 || res.status >= 400 || !res.bodyBase64) return null;
      return `data:image/jpeg;base64,${res.bodyBase64}`;
    } catch (_) {
      return null;
    }
  }, [isNative]);

  return {
    // state
    available,
    initializing,
    scanning,
    devices,
    connected,
    recording,
    status,
    wifi,
    downloadProgress,
    error,
    // BLE
    scan,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    refreshStatus,
    refreshSettings,
    setVideoResolution,
    setVideoFps,
    // Wi-Fi
    readWifiCredentials,
    enableAp,
    connectWifi,
    disconnectWifi,
    // HTTP
    listMedia,
    downloadClip,
    deleteClip,
    getThumbnail,
  };
}
